import { Category } from 'src/types/organization/category';

import {
  ParkingTicket,
  Visit,
  VisitFinishSetting,
  VisitLog,
  Visitor,
  VisitorNotificationMethod,
  VisitsResponse,
} from '../types/visits';

import api from '@/api/clients/api';
import { CreateVisitor } from '@/features/visitors/api/types/visitor';
import { Attachment } from '@/features/visits/api/types/attachment';
import { Statistic } from '@/types/organization/statistics';

class VisitsApi {
  async getVisits(page: number, perPage: number): Promise<VisitsResponse> {
    const { data } = await api.get<VisitsResponse>(`/visits?page=${page}&perPage=${perPage}`);

    return data;
  }

  async fetchVisit(visitId: number): Promise<Visit> {
    const { data } = await api.get<Visit>(`/visits/${visitId}`);

    return data;
  }

  async createVisit(
    visitors: CreateVisitor[],
    expectedAt: string,
    finishesAt: string,
    destinationIds: number[],
    hostId: number | null,
    visitFinishSetting: VisitFinishSetting | null,
    categoryId: number,
    description?: string,
  ): Promise<Visit> {
    const { data } = await api.post<Visit>(`/visits`, {
      visitors,
      expectedAt,
      finishesAt,
      destinationIds,
      hostId,
      visitFinishSetting,
      categoryId,
      description,
    });

    return data;
  }

  async getStatistics(from: string, to: string): Promise<Statistic[]> {
    const { data } = await api.get<Statistic[]>(`/statistics/visits?from=${from}&to=${to}`);

    return data;
  }
  async updateVisitor(
    visitorId: number,
    firstName: string,
    lastName: string,
    email: string,
  ): Promise<Visitor> {
    const { data } = await api.patch(`/visitor/${visitorId}`, {
      firstName,
      lastName,
      email,
    });

    return data;
  }

  async cancelVisit(visitId: number): Promise<Visit> {
    const { data } = await api.delete<Visit>(`/visits/${visitId}/cancel`);

    return data;
  }

  async finishVisit(visitId: number): Promise<Visit> {
    const { data } = await api.delete<Visit>(`/visits/${visitId}/finish`);

    return data;
  }

  async confirmVisit(visitId: number): Promise<Visit> {
    const { data } = await api.patch<Visit>(`/visits/${visitId}/confirm`);

    return data;
  }

  async rejectVisit(visitId: number): Promise<Visit> {
    const { data } = await api.patch<Visit>(`/visits/${visitId}/reject`);

    return data;
  }

  async assignParkingTicket(visitorId: number, ticketNumber: number): Promise<Visit> {
    const { data } = await api.post<Visit>(
      `/visitor/${visitorId}/assign-parking-ticket/ticket-number`,
      {
        ticketNumber,
      },
    );

    return data;
  }

  async applyDiscountForParkingTicket(visitorId: number): Promise<Visit> {
    const { data } = await api.post<Visit>(`/visitor/${visitorId}/apply-parking-ticket-discount`);

    return data;
  }

  async settleParkingTicket(visitorId: number, ticketNumber: number): Promise<ParkingTicket> {
    const { data } = await api.post<ParkingTicket>(`/visitors/${visitorId}/settle-parking-ticket`, {
      ticketNumber,
    });

    return data;
  }

  async fetchVisitLogs(visitorId: number): Promise<VisitLog[]> {
    const { data } = await api.get<VisitLog[]>(`/visitor/${visitorId}/authorizations`);

    return data;
  }

  async extendVisit(visitId: number, to: Date): Promise<Visit> {
    const { data } = await api.patch<Visit>(`/visits/${visitId}/prolong`, {
      to,
    });

    return data;
  }

  async fetchVisitCategories(): Promise<Category[]> {
    const { data } = await api.get<Category[]>(`/visit-categories`);

    return data;
  }

  async fetchVisitAttachments(visitId: number): Promise<Attachment[]> {
    const { data } = await api.get(`/visit/${visitId}/attachment`);

    return data;
  }

  async createAttachment(visitId: string, file: File): Promise<void> {
    const formData = new FormData();

    formData.append('file', file, file.name);

    await api.post(`/visit/${visitId}/attachment`, formData);
  }

  async removeAttachment(visitId: number, attachmentId: number): Promise<void> {
    await api.delete(`/visit/${visitId}/attachment/${attachmentId}`);
  }

  async resendVisitorInvitation(
    visitorId: number,
    method: VisitorNotificationMethod,
  ): Promise<void> {
    await api.post(`/visitor/${visitorId}/resend/${method}`);
  }
}

export const visitsApi = new VisitsApi();
